import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import { copyObject } from 'infrastructure/functions/copy-object';
import FilterButton from 'components/atoms/filter-button';
import CcmConsoleNavigationHeader from 'components/organisms/ccm-console/navigation-header';

import s from './styles.module.scss';
import ClinicFilterForm from './form';

import type { ISelectedClinics } from './form';

interface IProps {
  agencyGuid?: string[];
  included?: string[];
  onChange: (props: ISelectedClinics) => void;
  showOnInteractiveTimeDashboard?: boolean;
  useAdminApi?: boolean;
  middleSlot?: React.ReactNode;
}

const CcmDashboardFilterPopover: React.FC<IProps> = ({
  agencyGuid,
  included = [],
  showOnInteractiveTimeDashboard,
  useAdminApi,
  onChange,
  middleSlot,
}) => {
  const { t } = useTranslation();

  const [opened, setOpened] = useState(false);
  const [selectedClinics, setSelectedClinics] = useState<ISelectedClinics>({
    included,
  });

  const clinicFilterCount = useMemo(() => {
    return (included?.length ?? 0) > 0 ? included.length : 0;
  }, [included]);

  const changeSelectedClinics = (val: ISelectedClinics) => {
    setSelectedClinics(val);
  };

  const normalizeSelectAll = (val: ISelectedClinics): ISelectedClinics => {
    const filters = copyObject(val);
    const all = (filters as any).all as string[] | undefined;
    if (Array.isArray(all) && Array.isArray(filters.included)) {
      if (filters.included.length === all.length) {
        filters.included = [];
      }
    }
    if (!filters.included?.length) filters.included = [];
    return filters;
  };

  const onApply = () => {
    const filters = normalizeSelectAll(selectedClinics);
    onChange(filters);
    setOpened(false);
  };

  const onCancel = () => {
    setSelectedClinics({ included });
    setOpened(false);
  };

  useEffect(() => {
    if (opened) setSelectedClinics({ included });
  }, [opened]);

  const popoverContent = useMemo(
    () => (
      <div className={s.popover}>
        <div className={s['popover-header']}>
          <div className={s['popover-title']}>
            <div>{t('labels.filterClinics')}</div>
            <div className={s['sub-title']}>
              {t('labels.filterClinicsSubTitle')}
            </div>
          </div>
        </div>

        <div className={s['popover-body']}>
          <ClinicFilterForm
            agencyGuid={agencyGuid}
            included={selectedClinics.included ?? []}
            onChange={changeSelectedClinics}
            useAdminApi={useAdminApi}
            showOnInteractiveTimeDashboard={showOnInteractiveTimeDashboard}
          />
        </div>

        <div className={s['popover-footer']}>
          <BaseButton
            label={t('controls.cancel')}
            type="secondary"
            onClick={onCancel}
            className={s.button}
            dataCy="clinicList-cancel-button"
          />
          <BaseButton
            label={t('controls.applyFilter')}
            onClick={onApply}
            className={s.button}
            dataCy="clinicList-apply-button"
          />
        </div>
      </div>
    ),
    [
      agencyGuid,
      included,
      onApply,
      onCancel,
      selectedClinics,
      showOnInteractiveTimeDashboard,
      t,
      useAdminApi,
    ],
  );

  return (
    <div className={s.toolbar}>
      <CcmConsoleNavigationHeader />
      {middleSlot}
      <FilterButton
        count={clinicFilterCount}
        popoverContent={popoverContent}
        open={opened}
        onOpenChange={setOpened}
        label={t('labels.filterClinics', 'Clinic Filters')}
      />
    </div>
  );
};

export default CcmDashboardFilterPopover;
